import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useTranslation } from 'react-i18next';

import './Contact.css';
import Header from '../../components/Header/Header';

const SEND_CONTACT_EMAIL_MUTATION = gql`
  mutation SendContactEmail(
    $name: String!
    $lastName: String!
    $email: String!
    $phone: String
    $company: String
    $address: String
    $reason: String!
    $message: String!
  ) {
    sendContactEmail(
      name: $name
      lastName: $lastName
      email: $email
      phone: $phone
      company: $company
      address: $address
      reason: $reason
      message: $message
    ) {
      success
      message
    }
  }
`;

export default function Contact() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    reason: searchParams.get("reason") || "",
    message: ""
  });

  const [sendContactEmail, { loading }] = useMutation(SEND_CONTACT_EMAIL_MUTATION);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name || !formData.lastName || !formData.email || !formData.reason || !formData.message) {
      setErrorMessage(t("contact.errorFillAll"));
      return;
    }

    if (!emailRegex.test(formData.email)) {
      setErrorMessage(t("contact.errorEmail"));
      return;
    }

    try {
      const { data } = await sendContactEmail({ variables: formData });
      if (data.sendContactEmail.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(t("contact.errorSendFailed"));
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(t("contact.errorGeneric"));
    }
  };

  const discordLink = "https://discord.gg/7Y95KsxN";

  return (
    <div>
      <Header />
      <div className="contact-wrapper">
        {!isSubmitted ? (
          <div className="contact-container">
            <div className="contact-header">
              <h1>{t("contact.title")}</h1>
              <p>{t("contact.subtitle")}</p>
            </div>

            <form className="contact-form-box" onSubmit={handleSubmit}>
              {errorMessage && <div className="error-message">{errorMessage}</div>}

              <div className="form-fields">
                {/* Row 1: Nombre y Apellido */}
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div className="form-group col-2" style={{ width: '100%' }}>
                    <label htmlFor="name">{t("contact.form.labelName")}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder={t("contact.form.placeholderName")}
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group col-2" style={{ width: '100%' }}>
                    <label htmlFor="lastName">{t("contact.form.labelLastName")}</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder={t("contact.form.placeholderLastName")}
                      value={formData.lastName}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                {/* Row 2: Email y Teléfono */}
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div className="form-group col-2" style={{ width: '100%' }}>
                    <label htmlFor="email">{t("contact.form.labelEmail")}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder={t("contact.form.placeholderEmail")}
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group col-2" style={{ width: '100%' }}>
                    <label htmlFor="phone">{t("contact.form.labelPhone")}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder={t("contact.form.placeholderPhone")}
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Row 3: Empresa y Dirección */}
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div className="form-group col-2" style={{ width: '100%' }}>
                    <label htmlFor="company">{t("contact.form.labelCompany")}</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      placeholder={t("contact.form.placeholderCompany")}
                      value={formData.company}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group col-2" style={{ width: '100%' }}>
                    <label htmlFor="address">{t("contact.form.labelAddress")}</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      placeholder={t("contact.form.placeholderAddress")}
                      value={formData.address}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Row 4: Motivo (Full width) */}
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div className="form-group col-full" style={{ width: '100%' }}>
                    <label htmlFor="reason">{t("contact.dropdown.label")}</label>
                    <select
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      className="form-input form-select"
                      required
                    >
                      <option value="">{t("contact.dropdown.placeholder")}</option>
                      <option value="Specialized AI Solutions">
                        {t("contact.dropdown.options.specializedAi")}
                      </option>
                      <option value="Technical Support">
                        {t("contact.dropdown.options.technicalSupport")}
                      </option>
                      <option value="General Purpose">
                        {t("contact.dropdown.options.generalPurpose")}
                      </option>
                      <option value="Otros">{t("contact.dropdown.options.others")}</option>
                    </select>
                  </div>
                </div>

                {/* Row 5: Mensaje (Full width) */}
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <div className="form-group col-full" style={{ width: '100%' }}>
                    <label htmlFor="message">{t("contact.form.labelMessage")}</label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder={t("contact.form.placeholderMessage")}
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input form-textarea"
                      rows="8"
                      cols="1"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? t("contact.form.buttonSending") : t("contact.form.buttonSend")}
                </button>
              </div>

              <div className="form-footer">
                <p>{t("contact.form.discordPrompt")}</p>
                <a href={discordLink} target="_blank" rel="noopener noreferrer" className="discord-link-btn">
                  {t("contact.discord.join")}
                </a>
              </div>
            </form>
          </div>
        ) : (
          <div className="contact-success-container">
            <div className="success-box">
              <h1>{t("contact.submitted.thankYou")}</h1>
              <p>{t("contact.submitted.followUp")}</p>
              <a href={discordLink} target="_blank" rel="noopener noreferrer" className="discord-link-btn">
                {t("contact.discord.join")}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
